/**
 * Universal Action Executor
 * Enables X-Assist to execute ANY app action via voice commands
 * Enterprise SaaS - Multi-tenant with full audit logging
 */

import { getCompanyId } from '../tenantContext';
import { authService } from '../auth/authService';
import { indexedDBService } from '../offline/indexedDB';
import { syncQueueManager } from '../offline/syncQueue';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/client';

export interface AppAction {
  id: string;
  type: 'navigate' | 'create' | 'update' | 'delete' | 'execute' | 'filter' | 'bulk';
  target: string; // 'well' | 'ticket' | 'route' | 'entry' | 'schedule' | 'user' | 'settings'
  handler: (params: any, context: AppContext) => Promise<ExecutionResult>;
  requiredParams: string[];
  optionalParams?: string[];
  examples: string[];
  requiresConfirmation?: boolean;
  permissions?: string[]; // Required user permissions
  tenantIsolated: boolean; // Enforce tenant isolation
}

export interface AppContext {
  currentScreen: string;
  selectedWell?: any;
  selectedTicket?: any;
  recentlyViewed: Array<{ type: string; id: string; timestamp: Date }>;
  userRole: string;
  userId: string;
  companyId: string;
  currentFilters?: any;
  pendingActions?: string[];
  wells?: any[];
}

export interface ExecutionResult {
  success: boolean;
  action: string;
  message: string;
  data?: any;
  error?: string;
  executionTime?: number;
  navigationTarget?: string; // Screen to navigate to
  formData?: Record<string, any>; // Pre-filled form fields
  nextActions?: string[]; // Suggested follow-up actions
}

export interface ActionLog {
  id?: string;
  companyId: string;
  userId: string;
  actionId: string;
  command: string;
  params: any;
  result: ExecutionResult;
  timestamp: Date;
  executionTime: number;
  context: Partial<AppContext>;
}

class ActionExecutor {
  private static instance: ActionExecutor;
  private actions: Map<string, AppAction> = new Map();
  private executionHistory: ActionLog[] = [];

  private constructor() {
    this.initializeDefaultActions();
  }

  static getInstance(): ActionExecutor {
    if (!ActionExecutor.instance) {
      ActionExecutor.instance = new ActionExecutor();
    }
    return ActionExecutor.instance;
  }

  /**
   * Register a new action
   */
  registerAction(action: AppAction): void {
    if (this.actions.has(action.id)) {
    }
    this.actions.set(action.id, action);
  }

  /**
   * Register multiple actions at once
   */
  registerActions(actions: AppAction[]): void {
    actions.forEach(action => this.registerAction(action));
  }

  /**
   * Get all registered actions
   */
  getAllActions(): AppAction[] {
    return Array.from(this.actions.values());
  }

  /**
   * Get actions by type
   */
  getActionsByType(type: AppAction['type']): AppAction[] {
    return this.getAllActions().filter(a => a.type === type);
  }

  /**
   * Find action by ID or examples
   */
  findAction(query: string): AppAction | null {
    // Direct ID match
    if (this.actions.has(query)) {
      return this.actions.get(query)!;
    }

    // Search by examples
    const lowerQuery = query.toLowerCase();
    for (const action of this.actions.values()) {
      if (action.examples.some(ex => ex.toLowerCase().includes(lowerQuery))) {
        return action;
      }
    }

    return null;
  }

  /**
   * Execute a command
   */
  async executeCommand(
    command: string,
    context: AppContext,
    actionId?: string
  ): Promise<ExecutionResult> {
    const startTime = performance.now();
    const companyId = context.companyId || getCompanyId();

    try {
      // 1. Find the action
      const action = actionId 
        ? this.actions.get(actionId) 
        : this.findAction(command);

      if (!action) {
        return {
          success: false,
          action: 'unknown',
          message: 'No matching action found',
          error: `Could not find action for: "${command}"`
        };
      }

      // 2. Check tenant isolation
      if (action.tenantIsolated && context.companyId !== companyId) {
        return {
          success: false,
          action: action.id,
          message: 'Tenant isolation violation',
          error: 'Action cannot be executed across tenants'
        };
      }

      // 3. Check permissions
      if (action.permissions && action.permissions.length > 0) {
        const hasPermission = await this.checkPermissions(
          context.userId,
          action.permissions
        );
        if (!hasPermission) {
          return {
            success: false,
            action: action.id,
            message: 'Permission denied',
            error: `Required permissions: ${action.permissions.join(', ')}`
          };
        }
      }

      // 4. Extract parameters
      const params = await this.extractParameters(command, action, context);

      // 5. Validate required parameters
      const validation = this.validateParams(params, action);
      if (!validation.valid) {
        return {
          success: false,
          action: action.id,
          message: 'Invalid parameters',
          error: validation.error
        };
      }

      // 6. Execute the action
      const result = await action.handler(params, context);
      const executionTime = performance.now() - startTime;

      // 7. Log execution
      await this.logExecution(
        command,
        action.id,
        params,
        result,
        context,
        executionTime
      );

      return {
        ...result,
        executionTime
      };

    } catch (error) {
      console.error('Action execution failed:', error);
      const executionTime = performance.now() - startTime;
      
      return {
        success: false,
        action: actionId || 'unknown',
        message: 'Execution failed',
        error: error instanceof Error ? error.message : 'Unknown error',
        executionTime
      };
    }
  }

  /**
   * Extract parameters from command using pattern matching
   */
  private async extractParameters(
    command: string,
    action: AppAction,
    context: AppContext
  ): Promise<Record<string, any>> {
    const params: Record<string, any> = {};
    const lowerCommand = command.toLowerCase();

    // Well ID extraction
    if (action.requiredParams.includes('wellId') || action.optionalParams?.includes('wellId')) {
      // Check context first
      if (context.selectedWell) {
        params.wellId = context.selectedWell.id;
        params.wellName = context.selectedWell.name;
      } else {
        // Extract from command
        const wellMatch = lowerCommand.match(/well\s+([a-z0-9\-]+)/i);
        if (wellMatch) {
          const wellIdentifier = wellMatch[1];
          const well = context.wells?.find(w => 
            w.name.toLowerCase().includes(wellIdentifier.toLowerCase()) ||
            w.id === wellIdentifier
          );
          if (well) {
            params.wellId = well.id;
            params.wellName = well.name;
          }
        }
      }
    }

    // Date extraction
    if (action.optionalParams?.includes('date')) {
      const datePatterns = [
        { pattern: /today/i, offset: 0 },
        { pattern: /tomorrow/i, offset: 1 },
        { pattern: /next\s+(\w+)/i, special: 'next' },
        { pattern: /(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/i, format: 'date' }
      ];

      for (const dp of datePatterns) {
        const match = lowerCommand.match(dp.pattern);
        if (match) {
          if (dp.offset !== undefined) {
            const date = new Date();
            date.setDate(date.getDate() + dp.offset);
            params.date = date.toISOString();
          } else if (dp.special === 'next') {
            // Handle "next Tuesday", "next week", etc.
            params.date = this.parseRelativeDate(match[1]);
          } else if (dp.format === 'date') {
            const [_, month, day, year] = match;
            const fullYear = year ? (year.length === 2 ? `20${year}` : year) : new Date().getFullYear();
            params.date = new Date(`${fullYear}-${month}-${day}`).toISOString();
          }
          break;
        }
      }
    }

    // Priority extraction
    if (action.optionalParams?.includes('priority')) {
      if (lowerCommand.includes('emergency') || lowerCommand.includes('urgent')) {
        params.priority = 'critical';
      } else if (lowerCommand.includes('high priority')) {
        params.priority = 'high';
      } else if (lowerCommand.includes('low priority')) {
        params.priority = 'low';
      } else {
        params.priority = 'medium';
      }
    }

    // Issue type extraction
    if (action.optionalParams?.includes('issueType')) {
      const issueTypes = ['pump failure', 'leak', 'electrical', 'maintenance', 'inspection'];
      for (const type of issueTypes) {
        if (lowerCommand.includes(type)) {
          params.issueType = type.replace(' ', '_');
          break;
        }
      }
    }

    // Description extraction (everything after the main command)
    if (action.optionalParams?.includes('description')) {
      params.description = command;
    }

    // Production values
    const oilMatch = lowerCommand.match(/(\d+(?:\.\d+)?)\s*(?:barrels?)?\s*(?:of\s+)?oil/i);
    if (oilMatch && action.optionalParams?.includes('oilVolume')) {
      params.oilVolume = parseFloat(oilMatch[1]);
    }

    const waterMatch = lowerCommand.match(/(\d+(?:\.\d+)?)\s*(?:barrels?)?\s*(?:of\s+)?water/i);
    if (waterMatch && action.optionalParams?.includes('waterVolume')) {
      params.waterVolume = parseFloat(waterMatch[1]);
    }

    const pressureMatch = lowerCommand.match(/(?:tubing\s+)?(?:pressure\s+)?(\d+)/i);
    if (pressureMatch && action.optionalParams?.includes('pressure')) {
      params.pressure = parseFloat(pressureMatch[1]);
    }

    return params;
  }

  /**
   * Parse relative dates like "next Tuesday", "next week"
   */
  private parseRelativeDate(relative: string): string {
    const now = new Date();
    const lower = relative.toLowerCase();

    const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayIndex = daysOfWeek.findIndex(d => d.startsWith(lower));

    if (dayIndex !== -1) {
      // Next occurrence of this day
      const currentDay = now.getDay();
      const daysUntil = (dayIndex - currentDay + 7) % 7 || 7;
      now.setDate(now.getDate() + daysUntil);
      return now.toISOString();
    }

    if (lower === 'week') {
      now.setDate(now.getDate() + 7);
      return now.toISOString();
    }

    if (lower === 'month') {
      now.setMonth(now.getMonth() + 1);
      return now.toISOString();
    }

    return now.toISOString();
  }

  /**
   * Validate parameters
   */
  private validateParams(
    params: Record<string, any>,
    action: AppAction
  ): { valid: boolean; error?: string } {
    for (const required of action.requiredParams) {
      if (params[required] === undefined || params[required] === null) {
        return {
          valid: false,
          error: `Missing required parameter: ${required}`
        };
      }
    }
    return { valid: true };
  }

  /**
   * Check user permissions
   */
  private async checkPermissions(
    userId: string,
    requiredPermissions: string[]
  ): Promise<boolean> {
    try {
      const user = await authService.getCurrentUser();
      if (!user) return false;

      // Superadmin has all permissions
      if (user.role === 'superadmin') return true;

      // Admin has most permissions
      if (user.role === 'admin') {
        const restrictedPerms = ['manage_users', 'delete_company'];
        return !requiredPermissions.some(p => restrictedPerms.includes(p));
      }

      // Check role-based permissions
      const rolePermissions: Record<string, string[]> = {
        manager: ['view_all', 'create', 'update', 'manage_tickets'],
        pumper: ['view', 'create_entry', 'view_wells', 'create_ticket'],
        viewer: ['view', 'view_reports']
      };

      const userPerms = rolePermissions[user.role] || [];
      return requiredPermissions.every(p => userPerms.includes(p));

    } catch (error) {
      console.error('Permission check failed:', error);
      return false;
    }
  }

  /**
   * Log action execution for analytics and learning
   */
  private async logExecution(
    command: string,
    actionId: string,
    params: any,
    result: ExecutionResult,
    context: AppContext,
    executionTime: number
  ): Promise<void> {
    const log: ActionLog = {
      companyId: context.companyId,
      userId: context.userId,
      actionId,
      command,
      params,
      result,
      timestamp: new Date(),
      executionTime,
      context: {
        currentScreen: context.currentScreen,
        selectedWell: context.selectedWell ? { id: context.selectedWell.id, name: context.selectedWell.name } : undefined
      }
    };

    // Store in memory
    this.executionHistory.push(log);

    // Store in IndexedDB for offline analysis
    try {
      await indexedDBService.add('action_logs', log);
    } catch (error) {
    }

    // Queue for Firestore sync
    try {
      await syncQueueManager.enqueue({
        type: 'ACTION_LOG',
        entity: 'action_logs',
        method: 'POST',
        url: `/api/tenants/${context.companyId}/action-logs`,
        payload: log,
        priority: 8, // Low priority
        status: 'pending'
      });
    } catch (error) {
    }
  }

  /**
   * Get execution history
   */
  getExecutionHistory(limit?: number): ActionLog[] {
    return limit 
      ? this.executionHistory.slice(-limit)
      : this.executionHistory;
  }

  /**
   * Get execution statistics
   */
  getStatistics(companyId?: string): {
    totalExecutions: number;
    successRate: number;
    avgExecutionTime: number;
    topActions: Array<{ actionId: string; count: number }>;
  } {
    const logs = companyId 
      ? this.executionHistory.filter(l => l.companyId === companyId)
      : this.executionHistory;

    const successful = logs.filter(l => l.result.success).length;
    const avgTime = logs.reduce((sum, l) => sum + l.executionTime, 0) / logs.length;

    const actionCounts = logs.reduce((acc, log) => {
      acc[log.actionId] = (acc[log.actionId] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topActions = Object.entries(actionCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([actionId, count]) => ({ actionId, count }));

    return {
      totalExecutions: logs.length,
      successRate: logs.length > 0 ? successful / logs.length : 0,
      avgExecutionTime: avgTime || 0,
      topActions
    };
  }

  /**
   * Initialize default actions
   */
  private initializeDefaultActions(): void {
    // Actions will be registered by App.tsx during initialization
  }
}

// Export singleton
export const actionExecutor = ActionExecutor.getInstance();
